<template>
    <div>
        <Modal size="lg" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">Cancel subscription</div>
            </template>

            <template #body>
                <div
                    class="flex flex-col gap-4 border-t border-gray-200 p-6"
                    data-cy="kiosk-cancel-subscription-modal"
                >
                    <div class="text-sm font-medium text-gray-800">
                        Which subscription do you want to cancel?
                    </div>

                    <div class="flex flex-col gap-2">
                        <label
                            v-for="subscription in subscriptions"
                            :key="subscription.id"
                            class="flex cursor-pointer items-start gap-3 rounded border p-3"
                            :class="
                                selectedId === subscription.id
                                    ? 'border-orange-500 bg-orange-50'
                                    : 'border-gray-200'
                            "
                        >
                            <input
                                v-model="selectedId"
                                type="radio"
                                name="kiosk-cancel-subscription"
                                class="mt-1 h-4 w-4 cursor-pointer border-gray-300 text-orange-500 focus:ring-orange-500"
                                :value="subscription.id"
                            />

                            <div class="flex flex-col gap-1">
                                <div class="flex items-center gap-2">
                                    <span
                                        class="text-sm font-medium text-gray-800"
                                    >
                                        {{ typeLabel(subscription) }}
                                    </span>

                                    <span
                                        class="rounded px-2 py-0.5 text-xs font-medium"
                                        :class="
                                            gatewayClass(
                                                subscription.payment_gateway
                                            )
                                        "
                                    >
                                        {{
                                            gatewayLabel(
                                                subscription.payment_gateway
                                            )
                                        }}
                                    </span>
                                </div>

                                <div class="text-xs text-gray-500">
                                    {{ subscription.subscription_plan_uuid }}
                                </div>

                                <div
                                    v-if="subscription.recurring_payment_request"
                                    class="text-xs text-gray-500"
                                >
                                    Billed by recurring payment requests
                                </div>

                                <div
                                    v-if="subscription.next_billing_date"
                                    class="text-xs text-gray-500"
                                >
                                    Next billing date:
                                    {{ formatDate(subscription.next_billing_date) }}
                                </div>

                                <div
                                    v-if="subscription.canceled_at"
                                    class="text-xs text-topol-red-600"
                                >
                                    Already scheduled to end
                                    {{ formatDate(subscription.canceled_at) }}
                                </div>
                            </div>
                        </label>
                    </div>

                    <div class="mt-2 text-sm font-medium text-gray-800">
                        When should it end?
                    </div>

                    <div class="flex flex-col gap-2">
                        <label
                            class="flex cursor-pointer items-start gap-3 text-sm text-gray-700"
                        >
                            <input
                                v-model="immediately"
                                type="radio"
                                name="kiosk-cancel-subscription-timing"
                                class="mt-1 h-4 w-4 cursor-pointer border-gray-300 text-orange-500 focus:ring-orange-500"
                                :value="true"
                            />

                            <span>
                                Immediately
                                <span class="block text-xs text-gray-500">
                                    The customer can start a new subscription
                                    right away.
                                </span>
                            </span>
                        </label>

                        <label
                            class="flex items-start gap-3 text-sm"
                            :class="
                                canCancelAtPeriodEnd
                                    ? 'cursor-pointer text-gray-700'
                                    : 'cursor-not-allowed text-gray-400'
                            "
                        >
                            <input
                                v-model="immediately"
                                type="radio"
                                name="kiosk-cancel-subscription-timing"
                                class="mt-1 h-4 w-4 border-gray-300 text-orange-500 focus:ring-orange-500 disabled:cursor-not-allowed"
                                :disabled="!canCancelAtPeriodEnd"
                                :value="false"
                            />

                            <span>
                                At the end of the billing period
                                <span
                                    v-if="canCancelAtPeriodEnd"
                                    class="block text-xs text-gray-500"
                                >
                                    Ends
                                    {{ formatDate(selectedNextBillingDate) }}.
                                    The customer keeps access until then and
                                    cannot subscribe again before that date.
                                </span>
                                <span
                                    v-else
                                    class="block text-xs text-gray-400"
                                >
                                    Not available — this subscription has no
                                    future billing date.
                                </span>
                            </span>
                        </label>
                    </div>

                    <div
                        v-if="selectedSubscription"
                        class="rounded bg-gray-50 p-3 text-xs text-gray-600"
                    >
                        {{ effectDescription }}
                    </div>
                </div>
            </template>

            <template #footer>
                <div
                    class="flex justify-end gap-2 border-t border-gray-200 p-6"
                >
                    <AtomicButton color="white" size="md" @click="emit('close')">
                        Close
                    </AtomicButton>

                    <AtomicButton
                        color="red"
                        size="md"
                        :disabled="selectedSubscription === undefined"
                        @click="submit"
                    >
                        Cancel subscription
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script lang="ts" setup>
import { format } from 'date-fns';
import ISubscription from '@/types/Subscription/ISubscription';

const props = defineProps<{
    subscriptions: ISubscription[];
}>();

const emit = defineEmits<{
    (event: 'close'): void;
    (
        event: 'cancel',
        payload: { subscriptionId: number; immediately: boolean }
    ): void;
}>();

const selectedId = ref<number | null>(props.subscriptions[0]?.id ?? null);
const immediately = ref(true);

const selectedSubscription = computed(() =>
    props.subscriptions.find(
        (subscription) => subscription.id === selectedId.value
    )
);

const selectedNextBillingDate = computed(
    () => selectedSubscription.value?.next_billing_date
);

const canCancelAtPeriodEnd = computed(() => {
    const nextBillingDate = selectedNextBillingDate.value;

    return (
        nextBillingDate !== undefined &&
        nextBillingDate !== null &&
        new Date(nextBillingDate) > new Date()
    );
});

// A subscription with no future billing date can only be ended now, so keep the
// timing choice in sync when the admin switches between subscriptions.
watch(canCancelAtPeriodEnd, (available) => {
    if (!available) {
        immediately.value = true;
    }
});

const typeLabel = (subscription: ISubscription): string => {
    if (subscription.type === 'pro') {
        return 'PRO';
    }

    if (subscription.type === 'plugin') {
        return 'Plugin';
    }

    return 'Additional users';
};

const gatewayLabel = (gateway: ISubscription['payment_gateway']): string => {
    if (gateway === 'stripe') {
        return 'Stripe';
    }

    if (gateway === 'braintree') {
        return 'Braintree';
    }

    return 'No gateway';
};

const gatewayClass = (gateway: ISubscription['payment_gateway']): string => {
    if (gateway === 'stripe') {
        return 'bg-indigo-100 text-indigo-700';
    }

    if (gateway === 'braintree') {
        return 'bg-blue-100 text-blue-700';
    }

    return 'bg-gray-100 text-gray-600';
};

const formatDate = (date: Date | string | undefined | null): string =>
    date ? format(new Date(date), 'd. L. yyyy') : '-';

const effectDescription = computed((): string => {
    const subscription = selectedSubscription.value;

    if (!subscription) {
        return '';
    }

    const gateway = gatewayLabel(subscription.payment_gateway);

    if (subscription.payment_gateway === 'none') {
        return immediately.value
            ? 'There is no gateway subscription to cancel, so only the Topol record is ended — right now.'
            : `There is no gateway subscription to cancel, so only the Topol record is ended, on ${formatDate(selectedNextBillingDate.value)}.`;
    }

    return immediately.value
        ? `Cancels the subscription at ${gateway} and ends it in Topol right now.`
        : `Cancels the subscription at ${gateway} (no further charges) and ends it in Topol on ${formatDate(selectedNextBillingDate.value)}.`;
});

const submit = (): void => {
    const subscription = selectedSubscription.value;

    if (!subscription) {
        return;
    }

    emit('cancel', {
        subscriptionId: subscription.id,
        immediately: immediately.value,
    });
};
</script>
